import React, { useCallback, useContext, useEffect, useState } from 'react';
import { getStoredMoldCount, getStoredMoldType, getStoredValue, saveToStorage, storeValue } from '../helpers/storage';
import DetailedCalculations from './DetailedCalculations';
import IntegerInput from './IntegerInput';
import MoldRectangle from './MoldRectangle';
import MoldRound from './MoldRound';
import MoldSilicone from './MoldSilicone';
import { MoldsPropType } from '../helpers/prop-types';
import { MoldType } from '../helpers/consts-and-types';
import PropTypes from 'prop-types';
import { SettingsContext } from '../providers/SettingsProvider';
import { UrlSettingsContext } from '../providers/UrlSettingsProvider';
import styles from './MoldContainer.module.scss';
import useLocalize from '../hooks/use-localize';

const MoldContainer = ({ crustVolume, id, molds, setCrustVolume, setVolume, title, volume }) => {
  const { localize } = useLocalize();
  const { setPartUrl } = useContext(UrlSettingsContext);
  const { areDetailsVisible, isCrustEnabled } = useContext(SettingsContext);

  const [selectedType, setSelectedType] = useState(() => getStoredMoldType(id, MoldType.Round));

  const [isAreaEnabled, setIsAreaEnabled] = useState(true);

  const [moldCount, setMoldCount] = useState(() => getStoredMoldCount(id, 1));

  const [unitArea, setUnitArea] = useState(0);
  const [unitVolume, setUnitVolume] = useState(selectedType === MoldType.Silicone ? () => getStoredValue(id, 'silicone', 'volume', 0) : 0);
  const [crustUnitArea, setCrustUnitArea] = useState(0);
  const [crustUnitVolume, setCrustUnitVolume] = useState(0);
  const [area, setArea] = useState(0);
  const [crustArea, setCrustArea] = useState(0);
  const [moldUrl, setMoldUrl] = useState('');
  const [crustUrl, setCrustUrl] = useState('');

  useEffect(() => {
    setPartUrl(id, `${moldCount}-${selectedType}-${moldUrl}-${crustUrl ?? '0'}`);
  }, [crustUrl, moldCount, moldUrl, selectedType, setPartUrl]);

  useEffect(() => {
    saveToStorage(`${id}-count`, moldCount);
  }, [moldCount]);

  useEffect(() => {
    if (selectedType === 2) {
      storeValue(id, 'silicone', [['volume', unitVolume]]);
    }
  }, [unitVolume]);

  useEffect(() => {
    if (moldCount !== '' && selectedType !== MoldType.Silicone) {
      setArea(moldCount * unitArea);
    }
  }, [moldCount, selectedType, unitArea]);

  useEffect(() => {
    if (moldCount !== '' && selectedType !== MoldType.Silicone) {
      setCrustArea(moldCount * crustUnitArea);
    }
  }, [moldCount, selectedType, crustUnitArea]);

  useEffect(() => {
    if (moldCount !== '') {
      setVolume(moldCount * unitVolume);
    }
  }, [moldCount, setVolume, unitVolume]);

  useEffect(() => {
    if (moldCount !== '' && selectedType !== MoldType.Silicone) {
      setCrustVolume(moldCount * crustUnitVolume);
    }
  }, [moldCount, selectedType, setCrustVolume, crustUnitVolume]);

  const setRoundMold = useCallback(() => {
    setIsAreaEnabled(true);
    setSelectedType(MoldType.Round);
  }, [setIsAreaEnabled, setSelectedType]);

  const setRectangleMold = useCallback(() => {
    setIsAreaEnabled(true);
    setSelectedType(MoldType.Rectangle);
  }, [setIsAreaEnabled, setSelectedType]);

  const setSiliconeMold = useCallback(() => {
    setIsAreaEnabled(false);
    setCrustArea(-1);
    setCrustVolume(-1);
    setMoldCount(getStoredMoldCount(id, 1));
    setUnitVolume(getStoredValue(id, 'silicone', 'volume', 1000));
    setSelectedType(MoldType.Silicone);
  }, [setCrustArea, setCrustVolume, setIsAreaEnabled, setMoldCount, setSelectedType, setUnitVolume]);

  return (
    <div className={styles.moldContainer}>
      <h2>{title}</h2>
      <div className={styles.grid}>
        <div>{localize('mold.mold_type')}</div>
        <div className={styles.buttons}>
          <input className={styles.moldSelector} checked={selectedType === MoldType.Round} id={`radio-round-${id}`} name={id} onChange={setRoundMold} type='radio' />
          <label htmlFor={`radio-round-${id}`}>{localize('mold.type.round')}</label>
          <input className={styles.moldSelector} checked={selectedType === MoldType.Rectangle} id={`radio-rectangle-${id}`} name={id} onChange={setRectangleMold} type='radio' />
          <label htmlFor={`radio-rectangle-${id}`}>{localize('mold.type.rectangular')}</label>
          <input className={styles.moldSelector} checked={selectedType === MoldType.Silicone} id={`radio-silicone-${id}`} name={id} onChange={setSiliconeMold} type='radio' />
          <label htmlFor={`radio-silicone-${id}`}>{localize('mold.type.silicone')}</label>
        </div>
        <div>{localize('mold.mold_count')}</div>
        <IntegerInput max='99' min='1' onChange={setMoldCount} value={moldCount} />
      </div>
      {selectedType === MoldType.Round && (
        <MoldRound
          area={unitArea}
          crustArea={crustUnitArea}
          crustVolume={crustUnitVolume}
          setArea={setUnitArea}
          setCrustArea={setCrustUnitArea}
          setCrustUrl={setCrustUrl}
          setCrustVolume={setCrustUnitVolume}
          setMoldUrl={setMoldUrl}
          setVolume={setUnitVolume}
          side={id}
          volume={unitVolume}
        />
      )}
      {selectedType === MoldType.Rectangle && (
        <MoldRectangle
          area={unitArea}
          crustArea={crustUnitArea}
          crustVolume={crustUnitVolume}
          setArea={setUnitArea}
          setCrustArea={setCrustUnitArea}
          setCrustUrl={setCrustUrl}
          setCrustVolume={setCrustUnitVolume}
          setMoldUrl={setMoldUrl}
          setVolume={setUnitVolume}
          side={id}
          volume={unitVolume}
        />
      )}
      {selectedType === MoldType.Silicone && <MoldSilicone molds={molds} setMoldUrl={setMoldUrl} setVolume={setUnitVolume} side={id} volume={unitVolume} />}
      <DetailedCalculations
        area={area}
        areDetailsVisible={areDetailsVisible}
        crustArea={crustArea}
        crustUnitArea={crustUnitArea}
        crustUnitVolume={crustUnitVolume}
        crustVolume={crustVolume}
        isAreaEnabled={isAreaEnabled}
        isCrustEnabled={isCrustEnabled}
        moldCount={moldCount}
        unitArea={unitArea}
        unitVolume={unitVolume}
        volume={volume}
      />
    </div>
  );
};

MoldContainer.propTypes = {
  crustVolume: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  molds: MoldsPropType.isRequired,
  setCrustVolume: PropTypes.func.isRequired,
  setVolume: PropTypes.func.isRequired,
  title: PropTypes.string,
  volume: PropTypes.number.isRequired,
};

export default MoldContainer;
